/*
 * The fetch wrapper.
 *
 * Two things it exists to centralise:
 *
 *   - Errors. The server always answers with ApiErrorBody, so a failure is turned
 *     into an ApiError carrying the machine-readable code, the human message and
 *     the per-field details. Components then show a field error next to the field
 *     rather than a toast saying "something went wrong".
 *
 *   - The access token. Admin calls need one; public calls must not send one, so
 *     the same origin serves both without an anonymous reader ever being asked to
 *     sign in. The token is supplied by a provider function rather than read from
 *     storage here, because MSAL owns the cache and silent renewal. Since
 *     2026-10-26 (S46) a public call MAY send the signed-in reader's token -
 *     `readerToken`, never required - so private articles reach their audiences.
 *
 * All paths are relative, so the client works wherever it is deployed and the dev
 * server's proxy makes development the same shape as production.
 */

import type { ApiErrorBody } from '@accotool-kb/shared';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, string[]>;
  readonly conflict: ApiErrorBody['error']['conflict'];

  constructor(
    status: number,
    code: string,
    message: string,
    details: Record<string, string[]> = {},
    conflict?: ApiErrorBody['error']['conflict'],
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.conflict = conflict;
  }

  /** True when reloading and retrying is the right advice. */
  get isConcurrencyConflict(): boolean {
    return this.status === 409 && (this.conflict === 'row-version' || this.conflict === 'impact-changed');
  }

  get isAuthProblem(): boolean {
    return this.status === 401 || this.status === 403;
  }

  /** The first message for a field, for rendering under an input. */
  fieldError(path: string): string | undefined {
    return this.details[path]?.[0];
  }
}

export type TokenProvider = () => Promise<string>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | Array<string | number> | null | undefined>;
  /** Supplies a bearer token. Omitted for public endpoints. */
  token?: TokenProvider;
  /**
   * A public read's OPTIONAL token (2026-10-26, S46): the signed-in reader's, so
   * the server can include the private articles their audiences may read. Unlike
   * `token`, a failure to get one is not an error - the request simply goes
   * without, as an anonymous reader's, because the public site must keep working
   * for somebody whose sign-in has lapsed.
   */
  readerToken?: TokenProvider;
  signal?: AbortSignal;
}

export function buildQuery(
  query: RequestOptions['query'],
): string {
  if (!query) return '';

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      params.set(key, value.join(','));
    } else {
      params.set(key, String(value));
    }
  }

  const encoded = params.toString();
  return encoded.length > 0 ? `?${encoded}` : '';
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  if (options.token) {
    // A failure here is an authentication failure, not a network one, and is
    // reported as such so the UI offers "sign in again" rather than "retry".
    try {
      headers.Authorization = `Bearer ${await options.token()}`;
    } catch (error) {
      throw new ApiError(
        401,
        'token-acquisition-failed',
        error instanceof Error
          ? `Could not get an access token: ${error.message}`
          : 'Could not get an access token.',
      );
    }
  } else if (options.readerToken) {
    try {
      headers.Authorization = `Bearer ${await options.readerToken()}`;
    } catch {
      // No token to be had: read as the anonymous reader.
    }
  }

  let response: Response;
  try {
    const init: RequestInit = {
      method: options.method ?? 'GET',
      headers,
    };
    if (options.body !== undefined) init.body = JSON.stringify(options.body);
    if (options.signal) init.signal = options.signal;

    response = await fetch(`/api${path}${buildQuery(options.query)}`, init);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(
      0,
      'network-error',
      'The knowledge base could not be reached. Check your connection and try again.',
    );
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text.length > 0 ? safeJsonParse(text) : null;

  if (!response.ok) {
    const body = payload as ApiErrorBody | null;
    const error = body?.error;
    throw new ApiError(
      response.status,
      error?.code ?? `http-${response.status}`,
      error?.message ?? `The request failed (${response.status}).`,
      error?.details ?? {},
      error?.conflict,
    );
  }

  return payload as T;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
